import React from "react";
import { View, Text, TouchableOpacity } from "react-native";
import { useNavigation } from "@react-navigation/native";
import Ionicons from "@expo/vector-icons/Ionicons";
import { LinearGradient } from "expo-linear-gradient";
import {
    CreateResponsiveStyle,
    DEVICE_SIZES,
    useDeviceSize,
} from "rn-responsive-styles";
import { AppColors } from "../../../../utility/AppColors";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppScreens } from "../../../Utility/AppScreens";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { ScreenBGGradientView } from "../../../../common/CommonView";

const PaymentFailed = ({ route }) => {
    const navigation = useNavigation();
    const styles = useStyles();
    const deviceSize = useDeviceSize();
    const { error, session_id } = route?.params || {};
    const isMobile = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

    const handleTryAgain = () => {
        // Navigate to profile page to retry purchase
        if (typeof window !== 'undefined') {
            window.location.href = '/profile';
        } else {
            navigateToScreen(navigation, AppScreens.PurchaseScreen);
        }
    };

    const handleGoHome = () => {
        // Force full navigation to dashboard to close modal
        if (typeof window !== 'undefined') {
            window.location.href = '/dashboard';
        } else {
            navigateToScreen(navigation, AppScreens.HomeScreen);
        }
    };

    return (
        <View style={{ flex: 1 }}>
            {/* Platform background gradient */}
            <ScreenBGGradientView />

            {/* Content overlay */}
            <View style={[styles.container, isMobile && { justifyContent: "flex-end" }]}>
                <LinearGradient
                    colors={["#111827", "#000000"]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={[
                        styles.mainView,
                        isMobile ? styles.mobileMainView : styles.desktopMainView,
                    ]}
                >
                    {/* Handle bar for mobile */}
                    {isMobile && <View style={styles.handleBar} />}

                    {/* Header */}
                    <View style={styles.headerRow}>
                        <Text style={[isMobile ? AppFontStyle.BOLD_ANYBODY_16 : AppFontStyle.BOLD_ANYBODY_18, { color: AppColors.white, flex: 1 }]}>
                            Payment Failed
                        </Text>
                        <TouchableOpacity onPress={handleGoHome} style={styles.closeButton}>
                            <Ionicons name="close" size={isMobile ? 20 : 24} color={AppColors.white} />
                        </TouchableOpacity>
                    </View>

                    {/* Separator */}
                    <View style={styles.separator} />

                    {/* Failed Icon */}
                    <View style={styles.iconContainer}>
                        <View style={styles.failedIcon}>
                            <Ionicons name="close" size={48} color={AppColors.white} />
                        </View>
                    </View>

                    {/* Message */}
                    <Text style={[AppFontStyle.REGULAR_ANYBODY_14, styles.messageText]}>
                        Unfortunately, your payment could not be processed.{"\n"}Please try again or contact support.
                    </Text>

                    {/* Error Info */}
                    {error && (
                        <View style={styles.errorContainer}>
                            <Text style={[AppFontStyle.REGULAR_ANYBODY_12, { color: "#F87171" }]}>
                                Error: {error}
                            </Text>
                        </View>
                    )}

                    {/* Buttons */}
                    <View style={[styles.buttonContainer, isMobile && { flexDirection: "column" }]}>
                        <TouchableOpacity style={styles.secondaryButton} onPress={handleGoHome}>
                            <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: "#DC2626", textAlign: "center" }]}>
                                Go to Home
                            </Text>
                        </TouchableOpacity>

                        <TouchableOpacity style={styles.primaryButton} onPress={handleTryAgain}>
                            <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: AppColors.white }]}>
                                Try Again
                            </Text>
                        </TouchableOpacity>
                    </View>
                </LinearGradient>
            </View>
        </View>
    );
};

export default PaymentFailed;

const useStyles = CreateResponsiveStyle(
    {
        container: {
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
        },
        mainView: {
            alignSelf: "center",
            gap: 12,
            alignItems: "center",
        },
        desktopMainView: {
            width: 550,
            borderRadius: 8,
            borderWidth: 1.6,
            borderColor: "rgba(239, 68, 68, 0.50)",
            padding: 24,
        },
        mobileMainView: {
            width: "100%",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            borderWidth: 1,
            borderBottomWidth: 0,
            borderColor: "#DC2626",
            padding: 20,
            paddingHorizontal: 20,
            shadowColor: "rgba(232, 232, 232, 0.15)",
            shadowOffset: { width: 0, height: -3 },
            shadowOpacity: 1,
            shadowRadius: 14,
            elevation: 10,
        },
        handleBar: {
            width: 40,
            height: 4,
            backgroundColor: "#6B7280",
            borderRadius: 2,
            alignSelf: "center",
            marginBottom: 8,
        },
        headerRow: {
            width: "100%",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
        },
        closeButton: {
            padding: 4,
        },
        separator: {
            width: "100%",
            height: 1,
            backgroundColor: "#374151",
        },
        iconContainer: {
            alignItems: "center",
            marginVertical: 16,
        },
        failedIcon: {
            width: 80,
            height: 80,
            borderRadius: 40,
            backgroundColor: "#DC2626",
            justifyContent: "center",
            alignItems: "center",
        },
        messageText: {
            color: "#9CA3AF",
            textAlign: "center",
            lineHeight: 22,
        },
        errorContainer: {
            backgroundColor: "rgba(220, 38, 38, 0.1)",
            padding: 16,
            borderRadius: 8,
            alignItems: "center",
            width: "100%",
            borderWidth: 1,
            borderColor: "rgba(220, 38, 38, 0.3)",
        },
        buttonContainer: {
            flexDirection: "row",
            gap: 16,
            width: "100%",
            marginTop: 8,
        },
        secondaryButton: {
            flex: 1,
            backgroundColor: "transparent",
            borderWidth: 1,
            borderColor: AppColors.white,
            paddingHorizontal: 16,
            paddingVertical: 12,
            borderRadius: 8,
            justifyContent: "center",
            alignItems: "center",
        },
        primaryButton: {
            flex: 1,
            backgroundColor: "#DC2626",
            paddingHorizontal: 16,
            paddingVertical: 12,
            borderRadius: 8,
            justifyContent: "center",
            alignItems: "center",
        },
    },
    {
        [DEVICE_SIZES.SM]: {
            container: {
                justifyContent: "flex-end",
            },
        },
        [DEVICE_SIZES.XS]: {
            container: {
                justifyContent: "flex-end",
            },
        },
    }
);
